import type { Address } from "@tastyworks/tastyworks-api"

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

export const formatDate = (date: Date | null | undefined): string => {
  if (!date) return ""
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

// birthDate is parsed as UTC midnight; format in UTC so it doesn't shift a day
// earlier in negative-offset timezones.
export const formatBirthDate = (date: Date | null | undefined): string => {
  if (!date) return ""
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  })
}

// MM/DD/YY, HH:MM AM/PM — keeps datetimes compact so the date and time
// of day are both visible in dense table cells. Customer Details uses
// formatDate ("Month D, YYYY") instead.
const DATETIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "2-digit",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
}

export const formatDateTime = (date: Date | null | undefined): string => {
  if (!date) return ""
  return date.toLocaleString("en-US", DATETIME_FORMAT_OPTIONS)
}

// Used for Customer Details "Last Sign In At" only. Long-form date matches
// the rest of the Customer Details pane; time + timezone are retained so
// agents can disambiguate sign-ins across zones.
export const formatDateTimeWithZone = (
  date: Date | null | undefined
): string => {
  if (!date) return ""
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  })
}

export const formatName = (
  firstName: string | undefined,
  lastName: string | undefined,
  middleName?: string
): string => {
  return [firstName, middleName, lastName].filter(Boolean).join(" ")
}

export const formatAddress = (address: Address | undefined): string => {
  if (!address) return ""
  return `${address.city} ${address.stateRegion} ${address.postalCode}`
}

export const formatReceivedTime = (date: string | undefined): string => {
  if (!date) return ""
  return new Date(date).toLocaleString("en-US", DATETIME_FORMAT_OPTIONS)
}

export const formatPosEff = (action: string): string => {
  if (action.includes("Close")) {
    return "CLOSE"
  } else if (action.includes("Open")) {
    return "OPEN"
  } else {
    return ""
  }
}

export const signForOrderAction = (action: string): "+" | "-" | "" => {
  if (action.startsWith("Buy")) return "+"
  if (action.startsWith("Sell")) return "-"
  return ""
}

export const formatCurrency = (value: number): string => {
  return currencyFormatter.format(value)
}

// Maps the raw options-level value from the trading status API to the same
// customer-facing names shown on my.tastytrade.com.
const TRADING_LEVEL_DISPLAY_NAMES: Record<string, string> = {
  "Covered And Cash Secured": "Limited",
  "Defined Risk Spreads": "Basic",
  "Defined Risk Spreads Plus Naked": "IRA The Works",
  "No Restrictions": "The Works",
}

export const formatTradingLevel = (level: string | undefined): string => {
  if (!level) return ""
  return TRADING_LEVEL_DISPLAY_NAMES[level] ?? level
}

// Most account type values already match customer-facing names; SEP is the
// one known exception (API returns "SEP", customers see "SEP IRA").
const ACCOUNT_TYPE_DISPLAY_NAMES: Record<string, string> = {
  SEP: "SEP IRA",
}

export const formatAccountType = (accountType: string | undefined): string => {
  if (!accountType) return ""
  return ACCOUNT_TYPE_DISPLAY_NAMES[accountType] ?? accountType
}
